import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
}

const VARIANT_STYLES: Record<ButtonVariant, string> = {
  primary:
    'bg-kw-cerulean text-white hover:bg-kw-cerulean-dark active:bg-kw-cerulean-dark shadow-kw-cerulean disabled:bg-kw-ghost disabled:shadow-none',
  secondary:
    'bg-white text-kw-shark border border-kw-border hover:bg-kw-bg active:bg-kw-bg disabled:opacity-50 disabled:cursor-not-allowed',
  ghost:
    'bg-transparent text-kw-cerulean hover:bg-kw-cerulean-light active:bg-kw-cerulean-light disabled:opacity-50',
  danger:
    'bg-kw-danger-text text-white hover:opacity-90 active:opacity-90 disabled:bg-kw-ghost disabled:cursor-not-allowed',
};

const SIZE_STYLES: Record<ButtonSize, string> = {
  // Mobile-friendly tap targets on every size — minimum 40px tall.
  sm: 'h-9 px-3 text-[13px]',
  md: 'h-10 px-4 text-[14px]',
  lg: 'h-12 px-5 text-[15px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', isLoading, className, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled ?? isLoading}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-kw-md font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kw-cerulean focus-visible:ring-offset-2',
          VARIANT_STYLES[variant],
          SIZE_STYLES[size],
          className,
        )}
        {...props}
      >
        {isLoading ? (
          <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : null}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
